import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { PLOT_SIZES, SECTOR_OPTIONS, PLOT_TYPES } from "../data/site";
import { trackEvent } from "../utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full bg-[#fbfcfe] border-[1.5px] border-[#e2e6ee] px-[13px] py-[11px] text-base text-navy placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-gold transition rounded-[9px]";

const EnquiryForm = ({ source = "website", dark = false, compact = false, testid = "enquiry-form" }) => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", plot_size: "", sector: "", plot_type: "", message: "" });
    const [honeypot, setHoneypot] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (honeypot) {
            toast.success("Enquiry received!");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, { ...form, source, page_url: window.location.href });
            trackEvent("enquiry_submit", { form_source: source, plot_type: form.plot_type || "unspecified", sector: form.sector || "unspecified" });
            trackEvent("generate_lead", { form_source: source });
            toast.success("Enquiry received!", {
                description: "Confirmation email sent. Our specialist calls within 15 minutes (Mon–Sat, 9:30 AM – 6:30 PM).",
            });
            setForm({ name: "", phone: "", email: "", plot_size: "", sector: "", plot_type: "", message: "" });
        } catch (err) {
            toast.error("Could not submit enquiry", {
                description: "Please call or WhatsApp us directly — we respond instantly.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} className={compact ? "space-y-3" : "space-y-3.5"} data-testid={testid}>
            <input
                type="text"
                name="company"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="absolute opacity-0 h-0 w-0 pointer-events-none"
                aria-hidden="true"
                data-testid={`${testid}-honeypot`}
            />
            <div className="grid sm:grid-cols-2 gap-3">
                <input required value={form.name} onChange={set("name")} placeholder="Your Full Name *" className={inputCls} data-testid={`${testid}-name-input`} />
                <input required value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp Number *" type="tel" className={inputCls} data-testid={`${testid}-phone-input`} />
            </div>
            <input required value={form.email} onChange={set("email")} placeholder="Email Address *" type="email" className={inputCls} data-testid={`${testid}-email-input`} />
            <div className="grid sm:grid-cols-2 gap-3">
                <select value={form.plot_type} onChange={set("plot_type")} className={`${inputCls} ${form.plot_type ? "" : "text-slate-400"}`} data-testid={`${testid}-type-select`} aria-label="Plot type">
                    <option value="">Plot Type</option>
                    {PLOT_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={form.plot_size} onChange={set("plot_size")} className={`${inputCls} ${form.plot_size ? "" : "text-slate-400"}`} data-testid={`${testid}-size-select`} aria-label="Plot size">
                    <option value="">Plot Size Interested</option>
                    {PLOT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            <select value={form.sector} onChange={set("sector")} className={`${inputCls} ${form.sector ? "" : "text-slate-400"}`} data-testid={`${testid}-sector-select`} aria-label="Sector interested">
                <option value="">Sector Interested</option>
                {SECTOR_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <textarea value={form.message} onChange={set("message")} placeholder="Message (budget, corner plot, park facing...)" rows={compact ? 2 : 3} className={inputCls} data-testid={`${testid}-message-input`} />
            <button
                type="submit"
                disabled={loading}
                data-testid={`${testid}-submit-button`}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:brightness-105 text-navy font-semibold text-sm uppercase tracking-widest px-5 py-3 transition-all disabled:opacity-60"
            >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Sending..." : "Request Call Back"}
            </button>
            <p className={`text-[11px] text-center ${dark ? "text-slate-400" : "text-slate-500"}`}>
                100% privacy. No spam. Verified plots only.
            </p>
        </form>
    );
};

export default EnquiryForm;
